'use client'

import React, { useEffect, type JSX } from 'react'
import { useRef, useState } from 'react'
import { useOnScreen } from '../hooks'

type Props = {
  handleOnScreen: () => void
  handleOnce?: boolean
  children: React.ReactNode
  className?: string
  id?: string
  threshold?: number | number[]
  as?: keyof JSX.IntrinsicElements | React.ComponentType<any> // Allow valid HTML element names or custom React components
}

const OnScreen = ({
  handleOnScreen,
  handleOnce = true,
  id,
  children,
  className,
  threshold = 1,
  as = 'div',
  ...rest
}: Props) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const [hasSeen, setHasSeen] = useState(false)
  const isIntersecting = useOnScreen(ref, undefined, threshold)
  const Element = as

  useEffect(() => {
    if (!hasSeen && isIntersecting) {
      handleOnScreen()
      if (handleOnce) {
        setHasSeen(true)
      }
    }
  }, [isIntersecting, hasSeen, handleOnScreen, handleOnce])

  return (
    <Element ref={ref} id={id} className={className} {...rest}>
      {children}
    </Element>
  )
}

// use hook when OnScreen div isn't compatible
export const useTrackingOnScreen = (
  handleOnScreen: () => void,
  threshold = 1,
  handleOnce = true
) => {
  const ref = useRef<HTMLDivElement | null>(null)
  const [hasSeen, setHasSeen] = useState(false)
  const isIntersecting = useOnScreen(ref, undefined, threshold)

  useEffect(() => {
    if (!hasSeen && isIntersecting) {
      handleOnScreen()
      if (handleOnce) {
        setHasSeen(true)
      }
    }
  }, [isIntersecting, hasSeen, handleOnScreen, handleOnce])

  return ref
}

export default OnScreen
